<template>
  <div class="page-container oauth-client-manage">
    <el-card>
      <div class="toolbar">
        <el-button
          size="mini"
          type="primary"
          icon="el-icon-plus"
          @click="add"
        >
          新增
        </el-button>
      </div>

      <app-table
        ref="table"
        :table-key="table_key"
        :tablep="table_params"
        :get-data="getList"
        :page-size="table_params.per_page"
        :page-sizes="[15, 30, 50]"
      />
    </el-card>

    <el-dialog
      :title="dialog_config.title"
      :visible.sync="dialog_config.is_show"
      :close-on-click-modal="false"
      width="560px"
    >
      <app-form
        v-if="dialog_config.is_show"
        :form-data="dialog_config.form_config"
        default-label-width="100px"
        @confirm="dialog_config.confirm"
        @cancel="dialog_config.is_show = false"
      />
    </el-dialog>

    <el-dialog
      title="新增成功，请保存 Client Secret"
      :visible.sync="secret_dialog.is_show"
      :close-on-click-modal="false"
      width="560px"
    >
      <el-alert
        title="client_secret 仅创建时返回，请妥善保存。关闭后页面不会再保留该密钥。"
        type="warning"
        show-icon
        :closable="false"
      />
      <pre class="secret-content">{{ secretContent }}</pre>
      <span slot="footer">
        <el-button
          size="mini"
          @click="closeSecretDialog"
        >
          关闭
        </el-button>
        <el-button
          size="mini"
          type="primary"
          @click="copySecret"
        >
          一键复制
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getOauthClientList,
  createOauthClient,
  updateOauthClient,
  deleteOauthClient,
  revokeOauthClientToken,
} from '@/api/oauth-client';
import { clone } from '@/utils';
import { formDataAssign } from '@/tools/form';

export default {
  name: 'OAuthClientManage',
  components: {
    'app-table': () => import('@/components/app-table'),
    'app-form': () => import('@/components/app-form'),
  },
  data() {
    return {
      table_key: require('./model/table-key')(this),
      table_params: {
        page: 1,
        limit: 15,
        per_page: 15,
      },
      scope_options: [],
      scope_label_map: {},
      dialog_config: {
        is_show: false,
        title: '新增',
        form_config: {},
        confirm: () => {},
      },
      secret_dialog: {
        is_show: false,
        client_id: '',
        client_secret: '',
      },
    };
  },
  computed: {
    secretContent() {
      return [
        `Client ID: ${this.secret_dialog.client_id}`,
        `Client Secret: ${this.secret_dialog.client_secret}`,
      ].join('\n');
    },
  },
  methods: {
    getList(params = {}) {
      const query = {
        page: params.page || 1,
        per_page: params.limit || params.per_page || this.table_params.per_page,
      };
      this.table_params.per_page = query.per_page;

      return getOauthClientList(query).then((response) => {
        this.scope_options = this.formatScopeOptions(response.available_scopes);
        const list = response.list || [];
        return {
          list,
          page_info: {
            total_number: response.total || response.total_number || list.length,
          },
        };
      });
    },
    formatScopeOptions(scopes = []) {
      this.scope_label_map = scopes.reduce((result, { key, name }) => {
        result[key] = name || key;
        return result;
      }, {});

      return scopes.map(({ key, name }) => ({
        label: name || key,
        value: key,
      }));
    },
    formatScopeText(scopes) {
      if (!Array.isArray(scopes) || !scopes.length) {
        return '-';
      }
      return scopes.map(scope => this.scope_label_map[scope] || scope).join('、');
    },
    getFormConfig(type) {
      return require('./model/form-config')({
        type,
        scopeOptions: clone(this.scope_options),
      });
    },
    refreshTable() {
      this.$refs.table && this.$refs.table.refer();
    },
    add() {
      Object.assign(this.dialog_config, {
        is_show: true,
        title: '新增 OAuth Client',
        form_config: this.getFormConfig('add'),
        confirm: (params) => {
          createOauthClient(params).then((response) => {
            this.dialog_config.is_show = false;
            if (response.client_secret) {
              this.$nextTick(() => {
                this.showSecretDialog(response);
              });
              return;
            }
            this.$message.success('新增成功');
            this.refreshTable();
          });
        },
      });
    },
    edit(row) {
      Object.assign(this.dialog_config, {
        is_show: true,
        title: '编辑 OAuth Client',
        form_config: this.getFormConfig('edit'),
        confirm: (params) => {
          const payload = {
            ...params,
            description: this.dialog_config.form_config.description.value,
          };
          updateOauthClient(row.id, payload).then(() => {
            this.$message.success('编辑成功');
            this.dialog_config.is_show = false;
            this.refreshTable();
          });
        },
      });
      formDataAssign(this.dialog_config.form_config, row);
    },
    delete(row) {
      this.$confirm(`确认删除 OAuth Client「${row.name}」？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        deleteOauthClient(row.id).then(() => {
          this.$message.success('删除成功');
          this.refreshTable();
        });
      });
    },
    disable(row) {
      this.$confirm(`确认禁用 OAuth Client「${row.name}」？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        revokeOauthClientToken(row.id).then(() => {
          this.$message.success('禁用成功');
          this.refreshTable();
        });
      });
    },
    enable(row) {
      this.$confirm(`确认启用 OAuth Client「${row.name}」？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        updateOauthClient(row.id, { status: 1 }).then(() => {
          this.$message.success('启用成功');
          this.refreshTable();
        });
      });
    },
    showSecretDialog(response) {
      Object.assign(this.secret_dialog, {
        is_show: true,
        client_id: response.client_id,
        client_secret: response.client_secret,
      });
    },
    closeSecretDialog() {
      Object.assign(this.secret_dialog, {
        is_show: false,
        client_id: '',
        client_secret: '',
      });
      this.refreshTable();
    },
    copySecret() {
      const input = document.createElement('textarea');
      input.value = this.secretContent;
      input.setAttribute('readonly', 'readonly');
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      const is_success = document.execCommand('copy');
      document.body.removeChild(input);
      this.$message[is_success ? 'success' : 'error'](is_success ? '复制成功' : '复制失败');
    },
  },
};
</script>

<style lang="scss" scoped>
.oauth-client-manage {
  padding: 20px;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.secret-content {
  margin: 16px 0 0;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #303133;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
